import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  DashboardApiResponse,
  DashboardFilters,
  DashboardGlobalFiltersResponse,
  DashboardSummaryRequest,
  DashboardSummaryResponse,
  DashboardContractActivitiesResponse,
} from '../models/supplier-dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class SupplierDashboardService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.backofficeApiUrl}/api/v1/dashboard-suppliers`;
  private contractsUrl = `${environment.backofficeApiUrl}/api/v1/contracts`;

  // ------------------------------------------------------------------
  // ENDPOINT 1 – Global Filters
  // GET /api/v1/suppliers/dashboard/global-filters
  // Restituisce i valori disponibili per i filtri globali della dashboard:
  //   - companies    → ContractGeneralInfo.involvedEntities
  //   - referents    → Supplier.axaBuyer  (Label form: "Referente AXA")
  //   - serviceTypes → Contract.serviceType (Label form: "Tipologia Servizio")
  // ------------------------------------------------------------------
  getGlobalFilters(): Observable<DashboardApiResponse<DashboardGlobalFiltersResponse>> {
    return this.http.get<DashboardApiResponse<DashboardGlobalFiltersResponse>>(
      `${this.baseUrl}/global-filters`,
    );
  }

  // ------------------------------------------------------------------
  // Widget Attività pendenti → usa ContractRegistryService.getAllContracts()
  // con params: entityType=SUPPLIER, status=ACTIVE,IN_RENEWAL
  // Non serve un metodo dedicato in questo service.
  // ------------------------------------------------------------------

  // ------------------------------------------------------------------
  // ENDPOINT 3 – Summary (tutti i widget grafici in una sola chiamata)
  // GET /api/v1/suppliers/dashboard/summary
  // Aggrega: suppliersByCompany, contractsStatus, spendingBySupplier,
  // suppliersByService. Chiamato al caricamento e ad ogni cambio filtro.
  //
  // Query params:
  //   companies[]    → ContractGeneralInfo.involvedEntities
  //   referents[]    → Supplier.axaBuyer
  //   serviceTypes[] → Contract.serviceType
  //   topN           → top-N fornitori per Spending (default 10)
  // ------------------------------------------------------------------
  getSummary(
    filters: DashboardSummaryRequest = {},
  ): Observable<DashboardApiResponse<DashboardSummaryResponse>> {
    const params = this.buildParams(filters);
    return this.http.get<DashboardApiResponse<DashboardSummaryResponse>>(
      `${this.baseUrl}/summary`,
      { params },
    );
  }

  // ------------------------------------------------------------------
  // ENDPOINT 2 – Attività pendenti
  // GET /api/v1/contracts?entityType=SUPPLIER&status=DRAFT,IN_RENEWAL,EXPIRING
  // Estende endpoint esistente con nuovi filtri dashboard.
  //
  // Query params:
  //   status            → comma-separated: DRAFT,IN_RENEWAL,EXPIRING
  //   involvedEntities[] → companies valueKey[]
  //   axaBuyer[]         → referents[]
  //   supplierTypeId[]   → serviceTypes valueKey[]
  //   page               → 0-based (Spring Pageable)
  //   size               → default 10
  // ------------------------------------------------------------------
  getContractActivities(
    filters: DashboardFilters = {},
    page = 0,
    size = 10,
  ): Observable<DashboardApiResponse<DashboardContractActivitiesResponse>> {
    let params = new HttpParams()
      .set('entityType', 'SUPPLIER')
      .set('status', 'DRAFT,IN_RENEWAL,EXPIRING')
      .set('page', String(page))
      .set('size', String(size));
    (filters.companies ?? []).forEach((v) => {
      params = params.append('involvedEntities[]', v);
    });
    (filters.referents ?? []).forEach((v) => {
      params = params.append('axaBuyer[]', v);
    });
    (filters.serviceTypes ?? []).forEach((v) => {
      params = params.append('supplierTypeId[]', v);
    });
    return this.http.get<DashboardApiResponse<DashboardContractActivitiesResponse>>(
      this.contractsUrl,
      { params },
    );
  }

  // Export attività – TODO: da implementare in seguito

  // ------------------------------------------------------------------
  // UTILITY – Build HttpParams from a filter object.
  // Array values are serialized as repeated query params: key[]=v1&key[]=v2
  // ------------------------------------------------------------------
  private buildParams(filters: Record<string, unknown> | DashboardSummaryRequest): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          params = params.append(`${key}[]`, String(v));
        });
      } else {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
